import { Injectable, OnDestroy, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { StateService } from './state.service';
import { tap, catchError } from 'rxjs/operators';
import { EMPTY } from 'rxjs';
import { NotificationStateService } from './notification.state';
import { ApiService } from '../services/api.service';
import { ErrorHandlerService } from '../services/error-handler.service';

export interface ServerModel {
  price: number;
  free_days: number;
  variant_description: string;
  spec1: string;
  spec2: string;
  spec3: string;
  spec4: string;
}

export interface ServerLocation {
  country: string;
  city: string;
  models: Record<string, ServerModel>;
}

export interface ServerProduct {
  [provider: string]: {
    locations: Record<string, ServerLocation>;
  };
}

export interface ApplicationProduct {
  variant_code: string;
  name: string;
  description: string;
  icon: string;
  price: number;
  category: string;
  features: string[];
}

export interface ProductsState {
  serverProducts: ServerProduct[];
  applicationProducts: ApplicationProduct[];
  storageProducts: any[];
  isLoadingServerProducts: boolean;
  isLoadingAppProducts: boolean;
  isLoadingStorageProducts: boolean;
  error: string | null;
}

const initialProductsState: ProductsState = {
  serverProducts: [],
  applicationProducts: [],
  storageProducts: [],
  isLoadingServerProducts: false,
  isLoadingAppProducts: false,
  isLoadingStorageProducts: false,
  error: null,
};

@Injectable({
  providedIn: 'root',
})
export class ProductsStateService extends StateService<ProductsState> implements OnDestroy {
  private http = inject(HttpClient);
  private notificationService = inject(NotificationStateService);
  private apiService = inject(ApiService);
  private errorHandler = inject(ErrorHandlerService);

  // Computed signals for derived state
  readonly serverProducts = this.selectSlice(state => state.serverProducts);
  readonly applicationProducts = this.selectSlice(state => state.applicationProducts);
  readonly storageProducts = this.selectSlice(state => state.storageProducts);
  readonly isLoadingServerProducts = this.selectSlice(state => state.isLoadingServerProducts);
  readonly isLoadingAppProducts = this.selectSlice(state => state.isLoadingAppProducts);
  readonly isLoadingStorageProducts = this.selectSlice(state => state.isLoadingStorageProducts);
  readonly error = this.selectSlice(state => state.error);

  // Computed signals for derived data — use JSON equality because
  // Object.keys() and Array.from() create new references on every evaluation.
  readonly serverProviders = this.selectSlice(
    state => {
      if (state.serverProducts.length === 0) return [];
      return Object.keys(state.serverProducts[0] || {});
    },
    { equal: (a, b) => JSON.stringify(a) === JSON.stringify(b) }
  );

  readonly applicationCategories = this.selectSlice(
    state => {
      const categories = new Set<string>();
      state.applicationProducts.forEach(app => {
        if (app.category) categories.add(app.category);
      });
      return Array.from(categories);
    },
    { equal: (a, b) => JSON.stringify(a) === JSON.stringify(b) }
  );

  constructor() {
    super(initialProductsState);
  }

  /**
   * Load server products
   */
  loadServerProducts(): void {
    this.setState({ isLoadingServerProducts: true, error: null });

    this.http
      .get<ServerProduct[]>(this.apiService.getUrl('products/servers'))
      .pipe(
        tap(products => {
          this.setState({ serverProducts: products, isLoadingServerProducts: false });
        }),
        catchError(error => {
          const msg = this.errorHandler.extractErrorMessage(error);
          this.setState({ isLoadingServerProducts: false, error: msg });
          this.notificationService.error(msg);
          return EMPTY;
        })
      )
      .subscribe();
  }

  /**
   * Load application products
   */
  loadApplicationProducts(): void {
    this.setState({ isLoadingAppProducts: true, error: null });

    this.http
      .get<ApplicationProduct[]>(this.apiService.getUrl('products/applications'))
      .pipe(
        tap(products => {
          this.setState({ applicationProducts: products, isLoadingAppProducts: false });
        }),
        catchError(error => {
          const msg = this.errorHandler.extractErrorMessage(error);
          this.setState({ isLoadingAppProducts: false, error: msg });
          this.notificationService.error(msg);
          return EMPTY;
        })
      )
      .subscribe();
  }

  /**
   * Load storage products
   */
  loadStorageProducts(): void {
    this.setState({ isLoadingStorageProducts: true, error: null });

    this.http
      .get<any[]>(this.apiService.getUrl('products/storage'))
      .pipe(
        tap(products => {
          this.setState({ storageProducts: products, isLoadingStorageProducts: false });
        }),
        catchError(error => {
          const msg = this.errorHandler.extractErrorMessage(error);
          this.setState({ isLoadingStorageProducts: false, error: msg });
          this.notificationService.error(msg);
          return EMPTY;
        })
      )
      .subscribe();
  }

  /**
   * Get server locations for a specific provider
   */
  getLocationsForProvider(provider: string): Record<string, ServerLocation> | null {
    const serverProduct = this.serverProducts().find(p => Object.keys(p)[0] === provider);
    return serverProduct ? serverProduct[provider].locations : null;
  }

  /**
   * Get server models for a specific provider and location
   */
  getModelsForLocation(provider: string, location: string): Record<string, ServerModel> | null {
    const locations = this.getLocationsForProvider(provider);
    return locations && locations[location] ? locations[location].models : null;
  }

  /**
   * Get application product by variant code
   */
  getApplicationByVariantCode(variantCode: string): ApplicationProduct | undefined {
    return this.applicationProducts().find(app => app.variant_code === variantCode);
  }

  /**
   * Get applications by category
   */
  getApplicationsByCategory(category: string): ApplicationProduct[] {
    return this.applicationProducts().filter(app => app.category === category);
  }

  /**
   * Load all product data
   */
  loadAllProducts(): void {
    this.loadServerProducts();
    this.loadApplicationProducts();
    this.loadStorageProducts();
  }

  ngOnDestroy(): void {
    // Cleanup if needed
  }
}
